import * as turf from '@turf/turf';
import Papa from 'papaparse';
import { fromArrayBuffer } from 'geotiff';
import type { FeatureCollection } from 'geojson';
import { GeoSummary, RasterSummary } from '../types';

const formatNumber = (value: number, digits = 2) => Math.round(value * 10 ** digits) / 10 ** digits;

const toCentroid = (feature: turf.AllGeoJSON): [number, number] | null => {
  try {
    const centroid = turf.centroid(feature);
    const coords = centroid.geometry.coordinates;
    if (coords.length >= 2) return [formatNumber(coords[0], 6), formatNumber(coords[1], 6)];
  } catch {
    return null;
  }
  return null;
};

export async function readGeoSummary(file: File): Promise<{ summary: GeoSummary; geojson: FeatureCollection }> {
  const text = await file.text();
  const geojson = JSON.parse(text) as FeatureCollection;
  const collection: FeatureCollection =
    geojson.type === 'FeatureCollection' ? geojson : { type: 'FeatureCollection', features: [] };
  const features = collection.features ?? [];
  const area = features.reduce((sum, feature) => {
    try {
      return sum + turf.area(feature as turf.AllGeoJSON);
    } catch {
      return sum;
    }
  }, 0);
  const centroid = features.length ? toCentroid(collection as turf.AllGeoJSON) : null;
  const bbox =
    features.length > 0
      ? (turf.bbox(collection as turf.AllGeoJSON) as [number, number, number, number])
      : null;
  const geometryTypes = [...new Set(features.map((feature) => feature.geometry?.type).filter(Boolean))];
  const summary: GeoSummary = {
    sourceName: file.name,
    featureCount: features.length,
    geometryType: geometryTypes.join(', ') || 'Unknown',
    areaHa: formatNumber(area / 10000, 2),
    centroid,
    bbox,
    pointCount: features.reduce((sum, feature) => {
      if (feature.geometry?.type === 'Point') return sum + 1;
      if (feature.geometry?.type === 'MultiPoint') return sum + (feature.geometry.coordinates?.length ?? 0);
      return sum;
    }, 0),
  };
  return { summary, geojson: collection };
}

function computeStats(values: number[]) {
  if (!values.length) return { min: 0, max: 0, mean: 0, stdDev: 0 };
  const min = values.reduce((m, v) => Math.min(m, v), Infinity);
  const max = values.reduce((m, v) => Math.max(m, v), -Infinity);
  const mean = values.reduce((sum, v) => sum + v, 0) / values.length;
  const variance = values.reduce((sum, v) => sum + (v - mean) ** 2, 0) / values.length;
  return {
    min: formatNumber(min, 4),
    max: formatNumber(max, 4),
    mean: formatNumber(mean, 4),
    stdDev: formatNumber(Math.sqrt(variance), 4),
  };
}

export async function readRasterSummary(file: File): Promise<RasterSummary> {
  const tiff = await fromArrayBuffer(await file.arrayBuffer());
  const image = await tiff.getImage();
  const width = image.getWidth();
  const height = image.getHeight();
  const bandCount = image.getSamplesPerPixel();
  const sampleWindowWidth = Math.min(width, 512);
  const sampleWindowHeight = Math.min(height, 512);
  const window = [0, 0, sampleWindowWidth, sampleWindowHeight] as [number, number, number, number];
  const bands = Array.from({ length: Math.min(4, bandCount) }, (_, index) => index);
  const raster = await image.readRasters({ window, samples: bands, interleave: true });
  const values = Array.from(raster as ArrayLike<number>).map((v) => Number(v));
  const stats = computeStats(values);

  let ndviMean: number | null = null;
  let ndviCoverage: number | null = null;
  if (bandCount >= 4) {
    const red = await image.readRasters({ window, samples: [2] });
    const nir = await image.readRasters({ window, samples: [3] });
    const redBand = red[0] as ArrayLike<number>;
    const nirBand = nir[0] as ArrayLike<number>;
    const ndviValues: number[] = [];
    let vegetation = 0;
    for (let i = 0; i < redBand.length; i += Math.max(1, Math.floor(redBand.length / 5000))) {
      const r = Number(redBand[i]);
      const n = Number(nirBand[i]);
      const denom = n + r;
      const ndvi = denom === 0 ? 0 : (n - r) / denom;
      ndviValues.push(ndvi);
      if (ndvi >= 0.35) vegetation += 1;
    }
    if (ndviValues.length) {
      ndviMean = formatNumber(ndviValues.reduce((sum, v) => sum + v, 0) / ndviValues.length, 4);
      ndviCoverage = formatNumber(vegetation / ndviValues.length, 4);
    }
  }

  const previewDataUrl = await rasterToPreview(values, sampleWindowWidth, sampleWindowHeight, bands.length);

  return {
    sourceName: file.name,
    width,
    height,
    bandCount,
    min: stats.min,
    max: stats.max,
    mean: stats.mean,
    stdDev: stats.stdDev,
    ndviMean,
    ndviCoverage,
    previewDataUrl,
  };
}

async function rasterToPreview(values: number[], width: number, height: number, selectedBandCount: number): Promise<string | null> {
  if (!width || !height || !values.length) return null;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const image = ctx.createImageData(width, height);
  const pixels = image.data;
  const pixelCount = width * height;
  const bandStride = selectedBandCount > 0 ? selectedBandCount : 1;
  const min = values.reduce((m, v) => Math.min(m, v), Infinity);
  const max = values.reduce((m, v) => Math.max(m, v), -Infinity);
  const range = max - min || 1;

  for (let i = 0; i < pixelCount; i += 1) {
    const base = i * 4;
    const raw = values[i * bandStride] ?? 0;
    const normalized = Math.max(0, Math.min(1, (raw - min) / range));
    const [r, g, b] =
      selectedBandCount >= 4
      ? heatColor(normalized)
      : grayscaleColor(normalized);
    pixels[base] = r;
    pixels[base + 1] = g;
    pixels[base + 2] = b;
    pixels[base + 3] = 255;
  }

  ctx.putImageData(image, 0, 0);
  return canvas.toDataURL('image/png');
}

function grayscaleColor(normalized: number): [number, number, number] {
  const value = Math.round(normalized * 255);
  return [value, value, value];
}

function heatColor(normalized: number): [number, number, number] {
  const r = Math.round(255 * Math.min(1, normalized * 1.4));
  const g = Math.round(255 * Math.min(1, Math.max(0, (normalized - 0.25) * 1.2)));
  const b = Math.round(255 * Math.max(0, 1 - normalized * 1.2));
  return [r, g, b];
}

export async function readCsvSummary(file: File): Promise<{ rows: number; columns: string[]; preview: Array<Record<string, string>> }> {
  const text = await file.text();
  const parsed = Papa.parse<Record<string, string>>(text, {
    header: true,
    skipEmptyLines: true,
  });
  const data = Array.isArray(parsed.data) ? parsed.data : [];
  const columns = parsed.meta.fields ?? [];
  return {
    rows: data.length,
    columns,
    preview: data.slice(0, 5),
  };
}
